
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  isNotNullish,
  notEmpty,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import {
  createTabErrorFinder,
  FieldValue,
  FormField,
  FormFieldComboBox,
  FormFieldFileInput,
  FormFieldRichTextInput,
  FormFieldUrlAliasInput,
  FormFooter,
  OptionType,
  SeoChangeEvent,
  SeoFieldGroup,
  TabType,
  TagerFormSubmitEvent,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";
import {
  DynamicField,
  FieldUnion,
  RepeaterIncomingValue,
  universalFieldUtils,
} from "@tager/admin-dynamic-field";

import {
  BrandType,
  CategoryType,
  FieldType,
  FullCategoryType,
  ProductTypeType,
} from "@/typings/model";
import {
  createCategory,
  getBrandList,
  getCategory,
  getCategoryList,
  getFieldList,
  getProductTypeList,
  switchCategoryType,
  updateCategory,
} from "@/services/requests";
import {
  categoryFieldConfig,
  convertCategoryListNoChildrenToOption,
  convertCategoryListToOption,
  convertCategoryToFormValues,
  convertCategoryToRepeaterIncomingValue,
  convertFormValuesToCategoryCreationPayload,
  convertFormValuesToCategoryUpdatePayload,
  convertListToOption,
  FormValues,
  getFieldOptionList,
  typeOptionList,
} from "@/views/CategoryForm/CategoryForm.helpers";
import { getCategoryFormUrl, getCategoryListUrl } from "@/utils/paths";
import { getWebsiteOrigin } from "@/utils/common";
import FilterTable from "@/views/CategoryForm/FilterTable";

import ConditionPage from "./components/ConditionPage.vue";
import SwitchCategoryType from "./components/SwitchCategoryType.vue";
import ThroughNavigation from "./components/ThroughNavigation.vue";

export default defineComponent({
  name: "CategoryForm",
  components: {
    Page,
    DynamicField,
    ConditionPage,
    SwitchCategoryType,
    ThroughNavigation,
    FormFooter,
    SeoFieldGroup,
    FilterTable,
    FieldValue,
    FormFieldComboBox,
    FormField,
    FormFieldUrlAliasInput,
    FormFieldFileInput,
    FormFieldRichTextInput,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const categoryId = computed<string>(
      () => route.params.categoryId as string
    );

    const isCreation = computed<boolean>(() => categoryId.value === "create");

    const [fetchCategory, { data: category, loading: isCategoryLoading }] =
      useResource<Nullable<FullCategoryType>>({
        fetchResource: () => getCategory(categoryId.value),
        initialValue: null,
        resourceName: "category",
      });

    const [
      fetchCategoryList,
      { data: categoryList, loading: isCategoryListLoading, error },
    ] = useResource<Array<CategoryType>>({
      fetchResource: () =>
        getCategoryList({
          pageNumber: 1,
          pageSize: 99999,
        }),
      initialValue: [],
      resourceName: "Category list",
    });

    const [
      fetchProductTypeList,
      { data: productTypeList, loading: isProductTypeListLoading },
    ] = useResource<Array<ProductTypeType>>({
      fetchResource: getProductTypeList,
      initialValue: [],
      resourceName: "Product Type list",
    });

    const [fetchBrandList, { data: brandList, loading: isBrandListLoading }] =
      useResource<Array<BrandType>>({
        fetchResource: getBrandList,
        initialValue: [],
        resourceName: "Brand list",
      });

    const [fetchField, { data: fieldList, loading: isFieldListLoading }] =
      useResource<Array<FieldType>>({
        fetchResource: getFieldList,
        initialValue: [],
        resourceName: "Field list",
      });

    onMounted(() => {
      fetchCategoryList();
      if (isCreation.value) return;
      fetchProductTypeList();
      fetchCategory();
      fetchBrandList();
      fetchField();
    });

    watch(categoryId, () => {
      fetchProductTypeList();
      fetchCategory();
      fetchBrandList();
      fetchField();
    });

    const categoryListNoChildrenOption = ref<
      Array<OptionType<Nullable<number>>>
    >(
      convertCategoryListNoChildrenToOption(
        categoryList.value,
        isCreation.value,
        categoryId.value
      )
    );

    const categoryOptionList = ref<Array<OptionType>>(
      convertCategoryListToOption(categoryList.value)
    );

    const productTypeOptionList = ref<Array<OptionType<number>>>(
      convertListToOption(productTypeList.value)
    );

    const brandListOption = ref<Array<OptionType<number>>>(
      convertListToOption(brandList.value)
    );

    const values = ref<FormValues>(
      convertCategoryToFormValues(
        category.value,
        categoryListNoChildrenOption.value,
        categoryOptionList.value
      )
    );

    const inputsCategoryFieldIncomingValue = computed<RepeaterIncomingValue>(
      () => convertCategoryToRepeaterIncomingValue(values.value.related)
    );

    const inputsCategoriesField = ref<FieldUnion>(
      universalFieldUtils.createFormField(
        categoryFieldConfig(categoryOptionList.value),
        inputsCategoryFieldIncomingValue.value
      )
    );

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    function updateInputCategoryTableValues() {
      inputsCategoriesField.value = universalFieldUtils.createFormField(
        categoryFieldConfig(categoryOptionList.value),
        inputsCategoryFieldIncomingValue.value
      );
    }

    watch(category, () => {
      values.value = convertCategoryToFormValues(
        category.value,
        categoryListNoChildrenOption.value,
        categoryOptionList.value
      );
      updateInputCategoryTableValues();
    });

    watch(categoryList, () => {
      categoryListNoChildrenOption.value =
        convertCategoryListNoChildrenToOption(
          categoryList.value,
          isCreation.value,
          categoryId.value
        );

      categoryOptionList.value = convertCategoryListToOption(
        categoryList.value
      );

      values.value = convertCategoryToFormValues(
        category.value,
        categoryListNoChildrenOption.value,
        categoryOptionList.value
      );

      updateInputCategoryTableValues();
    });

    watch(productTypeList, () => {
      productTypeOptionList.value = convertListToOption(productTypeList.value);

      values.value = convertCategoryToFormValues(
        category.value,
        categoryListNoChildrenOption.value,
        categoryOptionList.value
      );
    });

    watch(brandList, () => {
      brandListOption.value = convertListToOption(brandList.value);

      values.value = convertCategoryToFormValues(
        category.value,
        categoryListNoChildrenOption.value,
        categoryOptionList.value
      );
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToCategoryCreationPayload(
        values.value
      );

      const updateBody = convertFormValuesToCategoryUpdatePayload(
        values.value,
        inputsCategoriesField.value
      );
      const requestPromise = isCreation.value
        ? createCategory(creationBody)
        : updateCategory(categoryId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(getCategoryFormUrl({ categoryId: response.data.id }));
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getCategoryListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertCategoryToFormValues(
              null,
              categoryListNoChildrenOption.value,
              categoryOptionList.value
            );
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Категория успешно добавлена`
              : "Категория успешно обновлена",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при добавлении категории`
              : "Ошибка при обновлении категории",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(
      () =>
        isCategoryLoading.value ||
        isCategoryListLoading.value ||
        isBrandListLoading.value ||
        isFieldListLoading.value ||
        isProductTypeListLoading.value
    );

    /** Tabs */

    const tabList = computed<Array<TabType>>(() => {
      const hasErrors = createTabErrorFinder(errors.value);

      return [
        {
          id: "common",
          label: "Основная",
          hasErrors: hasErrors([
            "name",
            "parent",
            "image",
            "content",
            "urlPath",
          ]),
        },
        {
          id: "connections",
          label: "Связи",
          hasErrors: hasErrors([]),
        },
        values.value?.type?.value === "PRODUCT"
          ? {
              id: "filters",
              label: "Фильтры",
              hasErrors: hasErrors([]),
            }
          : null,
        values.value?.type?.value === "PRODUCT"
          ? {
              id: "conditions",
              label: "Условия",
              hasErrors: hasErrors([]),
            }
          : null,

        {
          id: "seo",
          label: "SEO",
          hasErrors: hasErrors([
            "pageTitle",
            "pageDescription",
            "openGraphImage",
          ]),
        },
      ].filter(notEmpty);
    });

    const selectedTabId = ref<string>(tabList.value[0].id);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Добавить категорию`
        : `Редактировать категорию "${values.value.name}"`;
    });

    const pagePath = computed<string>(() => {
      return getWebsiteOrigin() + "/catalog";
    });

    const headerButtonList = computed<
      Array<{ text: string; href: string; target?: string }>
    >(() =>
      [
        category.value
          ? {
              text: "Просмотреть на сайте",
              href: getWebsiteOrigin() + "/catalog" + category.value.urlPath,
              target: "_blank",
            }
          : null,
      ].filter(isNotNullish)
    );

    const isSwitchCategoryType = ref<boolean>(false);

    function handleSwitchCategoryType() {
      const shouldSwitchCategoryType = window.confirm(
        `Вы уверены, что хотите сменить тип у категории?`
      );

      if (category && category.value && shouldSwitchCategoryType) {
        isSwitchCategoryType.value = true;
        switchCategoryType(category.value.id)
          .then(() => {
            fetchCategory();
            isSwitchCategoryType.value = false;
          })
          .catch((error) => {
            console.error(error);
            isSwitchCategoryType.value = false;
          });
      }
    }

    function handleSearchEngineOptimization({
      title,
      description,
      image,
    }: SeoChangeEvent) {
      values.value.pageTitle = title;
      values.value.pageDescription = description;
      values.value.openGraphImage = image;
    }

    const fieldOptionList = ref<Array<OptionType<number>>>([]);

    watch(
      () => [values.value.conditions, fieldList.value],
      () => {
        if (!fieldList.value && !values.value) return;
        fieldOptionList.value = getFieldOptionList(
          fieldList.value,
          values.value.conditions
        );
      },
      { deep: true }
    );

    return {
      backButtonUrl: getCategoryListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      category,
      isCreation,
      pageTitle,
      tabList,
      selectedTabId,
      typeOptionList,
      categoryListNoChildrenOption,
      pagePath,
      headerButtonList,
      inputsCategoriesField,
      productTypeOptionList,
      brandListOption,
      categoryOptionList,

      fetchCategory,
      isSwitchCategoryType,
      handleSwitchCategoryType,

      handleSearchEngineOptimization,

      fieldOptionList,
    };
  },
});
